/**
 * Staff photo helpers for the admin dashboard: browser-side downscale before upload, the reject
 * reason rule, and initials for people without a photo. Limits mirror the backend's.
 */

export const PHOTO_MAX_BYTES = 1_000_000;
export const PHOTO_MAX_EDGE = 800;
export const PHOTO_JPEG_QUALITY = 0.85;
export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';
export const PHOTO_REJECT_PRESETS = ['Not your face', 'Too dark', 'Not a clear face'];

/** A photo we refuse before it ever reaches the server; the message is shown as-is. */
export class PhotoPrepError extends Error {}

/** Scale to fit `max` on the long side; never upscales. */
export const fitWithin = (width: number, height: number, max: number = PHOTO_MAX_EDGE) => {
    const scale = Math.min(1, max / Math.max(width, height));
    return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

const loadImage = (file: Blob): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new PhotoPrepError('Could not read that image')); };
    img.src = url;
});

/** Downscale to a JPEG of at most 800 px on the long side; refuses anything still over 1 MB. */
export const resizePhoto = async (file: File): Promise<Blob> => {
    if (!PHOTO_ACCEPT.split(',').includes(file.type)) throw new PhotoPrepError('Use a JPG, PNG or WEBP photo');
    const img = await loadImage(file);
    const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new PhotoPrepError('This browser cannot resize photos');
    // JPEG has no transparency: paint white first so PNG backgrounds do not turn black.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', PHOTO_JPEG_QUALITY));
    if (!blob) throw new PhotoPrepError('Could not resize that photo');
    if (blob.size > PHOTO_MAX_BYTES) throw new PhotoPrepError('The photo is still over 1 MB after resizing. Try a smaller one.');
    return blob;
};

/** The server's rule: 3–200 characters once trimmed. */
export const isValidRejectReason = (reason: string): boolean => {
    const n = reason.trim().length;
    return n >= 3 && n <= 200;
};

export const staffInitials = (firstName?: string, lastName?: string): string =>
    `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase();

/** The server's message when there is one, else the error's own, else the fallback. */
export const errorMessageOf = (error: any, fallback: string): string =>
    error?.response?.data?.message || error?.message || fallback;
